import React, { useEffect, useMemo, useRef, useState } from "react";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";

import { useToast } from "../../contexts/ToastContext";
import { formatAgentLogTime, parseAgentLogEntry } from "../../utils/agentLog";

const LOG_LINE_HEIGHT = 24;
const LOG_BODY_PADDING = 24;
const DEFAULT_VISIBLE_LINES = 10;

const AgentLog = ({
  lines = [],
  className = "",
  live = false,
  meta = "",
  compact = false,
  visibleLines = DEFAULT_VISIBLE_LINES,
}) => {
  const { success } = useToast();
  const [search, setSearch] = useState("");
  const [now, setNow] = useState(() => new Date());
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);
  const bodyHeight = visibleLines * LOG_LINE_HEIGHT + LOG_BODY_PADDING;

  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [live]);

  const searchQuery = search.trim();
  const searchPattern = useMemo(() => buildSearchPattern(searchQuery), [searchQuery]);
  const isSearching = !!searchPattern;

  const filteredLines = useMemo(() => {
    if (!searchPattern) return lines;
    return lines.filter((line) => searchPattern.test(parseAgentLogEntry(line).message));
  }, [lines, searchPattern]);

  const lineCountLabel = isSearching
    ? `${filteredLines.length} of ${lines.length} lines`
    : `${lines.length} lines`;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !atBottomRef.current || isSearching || compact) return;
    el.scrollTop = el.scrollHeight;
  }, [lines.length, live, now, isSearching, compact]);

  if (!lines.length && !live) return null;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };

  const copyLogs = async () => {
    if (!lines.length) return;
    const text = lines.map(formatAgentLogLine).join("\n");
    await navigator.clipboard.writeText(text);
    success("Copied", 2000);
  };

  return (
    <div className={className}>
      <div className="overflow-hidden rounded-lg bg-[#26211a] text-[#e8e2d6]">
        {!compact && (
          <div className="flex items-center gap-2 border-b border-[#e8e2d6]/10 px-3 py-2 text-[11px]">
            {meta && <span className="min-w-0 flex-1 truncate font-medium opacity-60">{meta}</span>}
            <div className={`flex min-w-0 items-center justify-end gap-2 ${meta ? "shrink-0" : "ml-auto flex-1"}`}>
              <span className="shrink-0 font-medium opacity-60">{lineCountLabel}</span>
              <button
                type="button"
                onClick={copyLogs}
                title="Copy all logs"
                disabled={!lines.length}
                className="shrink-0 rounded p-1 text-[#e8e2d6]/60 hover:bg-[#e8e2d6]/10 hover:text-[#e8e2d6] disabled:opacity-30"
              >
                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              </button>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search logs"
                className="!min-w-0 !max-w-[180px] flex-1 !rounded !border !border-[#e8e2d6]/15 !bg-[#1c1814] !px-2 !py-1 !text-[11px] !text-[#e8e2d6] placeholder:!text-[#e8e2d6]/40 focus:!border-[#e8e2d6]/30 focus:!outline-none sm:!max-w-[220px]"
              />
            </div>
          </div>
        )}
        <div
          ref={scrollRef}
          onScroll={compact ? undefined : handleScroll}
          className={`agent-log-scroll px-4 py-3 font-mono text-[11.5px] leading-6 ${compact ? "" : "overflow-y-auto"}`}
          style={compact ? undefined : { height: bodyHeight }}
        >
          {filteredLines.length ? (
            filteredLines.map((line, index) => (
              <LogLine key={index} line={line} searchPattern={searchPattern} />
            ))
          ) : (
            isSearching && <div className="opacity-50">No matching logs</div>
          )}
          {live && <LiveLine now={now} />}
        </div>
      </div>
    </div>
  );
};

const LogLine = ({ line, searchPattern }) => {
  const entry = parseAgentLogEntry(line);
  const time = formatAgentLogTime(entry.at);

  return (
    <div className="whitespace-pre-wrap break-words">
      {time && <span className="mr-2 opacity-50">{time}</span>}
      &gt; {highlightText(entry.message, searchPattern)}
    </div>
  );
};

const LiveLine = ({ now }) => (
  <div className="flex items-center leading-6">
    <span className="mr-2 opacity-50">{formatAgentLogTime(now.toISOString())}</span>
    <span>&gt;</span>
    <span
      className="ml-1 inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#e8e2d6]/30 border-t-[#e8e2d6]"
      aria-hidden="true"
    />
  </div>
);

const buildSearchPattern = (query) => {
  if (!query) return null;
  try {
    return new RegExp(query, "i");
  } catch {
    return new RegExp(escapeRegex(query), "i");
  }
};

const highlightText = (text, pattern) => {
  if (!pattern) return text;
  const globalPattern = new RegExp(
    pattern.source,
    pattern.flags.includes("g") ? pattern.flags : `${pattern.flags}g`
  );
  const parts = [];
  let lastIndex = 0;
  for (const match of text.matchAll(globalPattern)) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <mark key={match.index} className="rounded-sm bg-[#c1591a]/50 text-inherit">
        {match[0]}
      </mark>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
};

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const formatAgentLogLine = (line) => {
  const entry = parseAgentLogEntry(line);
  const time = formatAgentLogTime(entry.at);
  return time ? `${time} > ${entry.message}` : `> ${entry.message}`;
};

export default AgentLog;
